import React from 'react';
import { ActivityIndicator, Platform, TouchableOpacity, View } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';

import { Typography } from '../ui';
import { LivesCalendarFilters } from './LivesCalendarFilters';
import { fontWeights } from '../../styles/typographyTokens';

import { accentTextOn, ON_ACCENT_TEXT } from '../../styles/accentText';
type PathOption = {
  id: string;
  name: string;
};

type LivesCalendarHeaderProps = {
  accentColor: string;
  activeTypeFilters: string[];
  disablePrev: boolean;
  filterLabels: React.ComponentProps<typeof LivesCalendarFilters>['labels'];
  hasActiveFilters?: boolean;
  isDark: boolean;
  isLoading: boolean;
  isMobile: boolean;
  language: string;
  monthTitle: string;
  onClearPaths: () => void;
  onNextMonth: () => void;
  onPrevMonth: () => void;
  onReset: () => void;
  onToggleMyEvents: () => void;
  onTogglePath: (path: string) => void;
  onToggleType: (type: string) => void;
  openDropdown: 'paths' | null;
  pathOptions: PathOption[];
  selectedPaths: string[];
  setOpenDropdown: React.Dispatch<React.SetStateAction<'paths' | null>>;
  showMyEvents: boolean;
  showMyEventsToggle: boolean;
  themeColors: {
    textPrimary: string;
    textSecondary: string;
  };
};

export const LivesCalendarHeader: React.FC<LivesCalendarHeaderProps> = ({
  accentColor,
  activeTypeFilters,
  disablePrev,
  filterLabels,
  hasActiveFilters = false,
  isDark,
  isLoading,
  isMobile,
  language,
  monthTitle,
  onClearPaths,
  onNextMonth,
  onPrevMonth,
  onReset,
  onToggleMyEvents,
  onTogglePath,
  onToggleType,
  openDropdown,
  pathOptions,
  selectedPaths,
  setOpenDropdown,
  showMyEvents,
  showMyEventsToggle,
  themeColors,
}) => (
  <>
    <View
      testID="lives-calendar"
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 12,
        paddingTop: 16,
        paddingBottom: 12,
      }}
    >
      <TouchableOpacity
        onPress={onReset}
        activeOpacity={0.7}
        style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}
        {...Platform.select({ web: { style: { cursor: 'pointer' } } as any })}
      >
        <Typography
          variant="h3"
          style={{ color: themeColors.textPrimary, fontWeight: fontWeights.bold, letterSpacing: -0.5, fontSize: isMobile ? 20 : undefined } as any}
          numberOfLines={1}
        >
          {monthTitle}
        </Typography>
        {hasActiveFilters && !isMobile && (
          <View style={{ width: 7, height: 7, borderRadius: 3.5, backgroundColor: accentColor, marginBottom: 2 }} />
        )}
        {isLoading && <ActivityIndicator size="small" color={accentColor} style={{ marginLeft: 8 }} />}
      </TouchableOpacity>

      {!isMobile && (
        <LivesCalendarFilters
          accentColor={accentColor}
          activeTypeFilters={activeTypeFilters}
          compact
          isDark={isDark}
          labels={filterLabels}
          onClearPaths={onClearPaths}
          onTogglePath={onTogglePath}
          onToggleType={onToggleType}
          openDropdown={openDropdown}
          pathOptions={pathOptions}
          selectedPaths={selectedPaths}
          setOpenDropdown={setOpenDropdown}
          showDropdownPanel={false}
          themeColors={themeColors}
        />
      )}

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: isMobile ? 6 : 12 }}>
        {showMyEventsToggle && (
          <TouchableOpacity
            onPress={onToggleMyEvents}
            activeOpacity={0.7}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: isMobile ? 0 : 7,
              paddingVertical: 9,
              paddingHorizontal: isMobile ? 10 : 14,
              backgroundColor: showMyEvents ? (isDark ? 'rgba(233, 130, 69,0.12)' : '#FFF0E6') : accentColor,
              borderColor: accentColor,
              borderWidth: 1,
              borderRadius: 24,
              ...Platform.select({ web: { cursor: 'pointer', transition: 'all 0.15s ease' } as any }),
            }}
          >
            <Ionicons name="calendar-outline" size={16} color={showMyEvents ? accentTextOn(accentColor) : ON_ACCENT_TEXT} />
            {!isMobile && (
              <Typography
                variant="bodySmall"
                style={{ color: showMyEvents ? accentTextOn(accentColor) : ON_ACCENT_TEXT, fontSize: 13, fontWeight: fontWeights.bold } as any}
              >
                {showMyEvents
                  ? (language === 'ru' ? 'Все события' : 'All Events')
                  : (language === 'ru' ? 'Мои события' : 'My Events')}
              </Typography>
            )}
          </TouchableOpacity>
        )}

        <View style={{ flexDirection: 'row', gap: isMobile ? 6 : 12 }}>
          <TouchableOpacity
            style={{
              width: isMobile ? 32 : 44,
              height: isMobile ? 32 : 44,
              borderRadius: isMobile ? 16 : 22,
              borderWidth: 1,
              alignItems: 'center',
              justifyContent: 'center',
              borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              opacity: disablePrev ? 0.3 : 1,
              ...Platform.select({ web: { cursor: 'pointer', transition: 'all 0.2s ease' } as any }),
            }}
            onPress={onPrevMonth}
            activeOpacity={0.7}
            disabled={disablePrev}
          >
            <Ionicons name="chevron-back" size={isMobile ? 14 : 20} color={themeColors.textPrimary} />
          </TouchableOpacity>
          <TouchableOpacity
            style={{
              width: isMobile ? 32 : 44,
              height: isMobile ? 32 : 44,
              borderRadius: isMobile ? 16 : 22,
              borderWidth: 1,
              alignItems: 'center',
              justifyContent: 'center',
              borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              ...Platform.select({ web: { cursor: 'pointer', transition: 'all 0.2s ease' } as any }),
            }}
            onPress={onNextMonth}
            activeOpacity={0.7}
          >
            <Ionicons name="chevron-forward" size={isMobile ? 14 : 20} color={themeColors.textPrimary} />
          </TouchableOpacity>
        </View>
      </View>
    </View>

    {isMobile && (
      <LivesCalendarFilters
        accentColor={accentColor}
        activeTypeFilters={activeTypeFilters}
        isDark={isDark}
        labels={filterLabels}
        onClearPaths={onClearPaths}
        onTogglePath={onTogglePath}
        onToggleType={onToggleType}
        openDropdown={openDropdown}
        pathOptions={pathOptions}
        selectedPaths={selectedPaths}
        setOpenDropdown={setOpenDropdown}
        themeColors={themeColors}
      />
    )}

    {!isMobile && openDropdown === 'paths' && (
      <LivesCalendarFilters
        accentColor={accentColor}
        activeTypeFilters={activeTypeFilters}
        isDark={isDark}
        labels={filterLabels}
        onClearPaths={onClearPaths}
        onTogglePath={onTogglePath}
        onToggleType={onToggleType}
        openDropdown={openDropdown}
        pathOptions={pathOptions}
        selectedPaths={selectedPaths}
        setOpenDropdown={setOpenDropdown}
        showPills={false}
        themeColors={themeColors}
      />
    )}
  </>
);
